import { useCallback, useRef, useState } from "react";
import { RemoteAudioTrack, Room, RoomEvent } from 'livekit-client';

import { Room as ClientRoom } from "./../../models/room";
import { CALL_STATES, SUB_BUTTON_ACTIONS } from "./../../constants";

interface ClientDetails {
    room: Room;
    webUrl: string;
    token: string;
}

const playAudioTrack = (track: RemoteAudioTrack) => {
    const audioEl = document.createElement('audio');
    audioEl.srcObject = new MediaStream([track.mediaStreamTrack]);
    audioEl.autoplay = true;
    audioEl.muted = false;
    document.body.appendChild(audioEl);
    audioEl.play().catch(err => console.log("Audio play error:", err));
};

const useParticipant = (handleToggleActiveButtonActions, handleSetActiveState, handleUpdateActiveToken, handleClearFrame) =>{
    const [participant , setParticipant] = useState({});
    const [remoteParticipants, setRemoteParticipants] = useState(new Map());
    const registeredRoomRef = useRef<Room | null>(null);

    // Registers all event listeners — call BEFORE connect()
    const handleSetParticipant = useCallback((incomingClientDetails: ClientDetails)=>{

        const { room : clientRoom } = incomingClientDetails || {};

        if(!clientRoom) return;

        if (registeredRoomRef.current === clientRoom) return;
        registeredRoomRef.current = clientRoom;

        clientRoom.on(RoomEvent.TrackSubscribed, (track, pub, remoteParticipant) => {
            console.log('Track subscribed:', track.kind, 'from', remoteParticipant.identity);
            if (track.kind === 'audio') {
                playAudioTrack(track as RemoteAudioTrack);
            }
        });

        clientRoom.on(RoomEvent.ParticipantConnected, (p) => {
            console.log('Participant connected:', p.identity, 'kind:', p.kind);
            setRemoteParticipants(prev => new Map(prev).set(p.sid, p));
            // Read current SIP state immediately — event may have already fired before we connected
            const sipStatus = p.attributes?.['sip.callStatus'];
            if (sipStatus) handleSetActiveState(sipStatus);
        });

        clientRoom.on(RoomEvent.ParticipantDisconnected, (p) => {
            console.log('Participant disconnected:', p.identity);
            setRemoteParticipants(prev => {
                const next = new Map(prev);
                next.delete(p.sid);
                return next;
            });
        });

        // Fires when a remote participant publishes a track (before subscription)
        // SIP participants publish audio when the call is answered
        clientRoom.on(RoomEvent.TrackPublished, (pub, participant) => {
            console.log('Track published by:', participant.identity, 'kind:', pub.kind, 'participant kind:', participant.kind);
            // setRemoteParticipants(prev => new Map(prev).set(participant.sid, participant));
        });

        // Fires when SIP participant attributes change (e.g. ringing → active)
        clientRoom.on(RoomEvent.ParticipantAttributesChanged, (changedAttributes, participant) => {
            console.log('Participant attributes changed:', participant.identity,"NEW ATTRIBUTES ARE : ", changedAttributes,"    ",changedAttributes?.['sip.callStatus']);
            handleSetActiveState(changedAttributes?.['sip.callStatus']);
            // setRemoteParticipants(prev => new Map(prev).set(participant.sid, participant));
        });

        clientRoom.on(RoomEvent.ConnectionStateChanged, (state) => {
            console.log('Connection state:', state);
            if(state === "disconnected"){
                ClientRoom.getInstance().getRoomInstance().disconnect();
                handleToggleActiveButtonActions(SUB_BUTTON_ACTIONS.END);
                handleSetActiveState?.(CALL_STATES.ENDED);
                handleUpdateActiveToken("");
                handleClearFrame();
            }
        });

    },[handleSetActiveState, handleToggleActiveButtonActions, handleUpdateActiveToken, handleClearFrame]);

    // Syncs local participant + existing remote participants — call AFTER connect()
    const handleSyncAfterConnect = useCallback((room: Room) => {
        setParticipant(room.localParticipant);

        console.log('Remote participants at connect time:', room.remoteParticipants.size, Array.from(room.remoteParticipants.values()).map(p => p.identity));
        setRemoteParticipants(new Map(room.remoteParticipants));

        room.remoteParticipants.forEach((remoteParticipant) => {
            // Read current SIP state — participant was already here before we connected
            const sipStatus = remoteParticipant.attributes?.['sip.callStatus'];
            if (sipStatus) {
                console.log('Initial SIP status for', remoteParticipant.identity, ':', sipStatus);
                handleSetActiveState(sipStatus);
            }

            remoteParticipant.trackPublications.forEach((publication) => {
                if (publication.isSubscribed && publication.track && publication.kind === 'audio') {
                    console.log('Playing existing audio track from:', remoteParticipant.identity);
                    playAudioTrack(publication.track as RemoteAudioTrack);
                }
            });
        });
    }, [handleSetActiveState]);

    return {
        handleSetParticipant,
        handleSyncAfterConnect,
        participant,
        remoteParticipants
    }
}

export {
    useParticipant,
}
